import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectRouteCoordinates } from '../mapSlice';
import { calculateBearing, calculateDistance } from '../logic/NavigationHelpers';

/**
 * Generates a simulation script from route coordinates
 * Converts route points into stepwise GPS updates with timing
 */
const generateRouteSimulationScript = (routeCoordinates) => {
    if (!routeCoordinates || routeCoordinates.length < 2) {
        return '// No route available. Please create a route first.';
    }

    // Sample the route to avoid overwhelming number of points
    const sampleInterval = Math.max(1, Math.floor(routeCoordinates.length / 100)); // Max 100 steps
    const sampledPoints = routeCoordinates.filter((_, index) => index % sampleInterval === 0);

    // Always include the last point
    if (routeCoordinates[routeCoordinates.length - 1] !== sampledPoints[sampledPoints.length - 1]) {
        sampledPoints.push(routeCoordinates[routeCoordinates.length - 1]);
    }

    // Generate steps with bearing and timing
    const steps = [];
    const speedKmh = 50; // Simulated driving speed
    const speedMs = speedKmh / 3.6; // Convert to m/s

    for (let i = 0; i < sampledPoints.length; i++) {
        const point = sampledPoints[i];
        const nextPoint = sampledPoints[i + 1];

        let heading = 0;
        let delay = 1000; // Default 1 second

        if (nextPoint) {
            heading = calculateBearing(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
            const distance = calculateDistance(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
            delay = Math.round((distance / speedMs) * 1000); // Time in ms to cover distance
            delay = Math.max(500, Math.min(delay, 5000)); // Clamp between 0.5s and 5s
        }

        steps.push({
            lat: point.lat,
            lng: point.lng,
            heading: Math.round(heading),
            speed: speedMs,
            delay: delay
        });
    }

    // Generate the actual JavaScript code
    const stepsJson = JSON.stringify(steps, null, 2);

    return `// Route Simulation Script - Generated from current route
// WORKFLOW:
// 1. Paste this script into console (sets GPS to route start immediately)
// 2. Click "START DRIVING" button
// 3. In console, run: startSimulation()

// Store route steps globally
window.routeSteps = ${stepsJson};

// Set initial GPS position to route start IMMEDIATELY
if (typeof window.updateGPS === 'function') {
    console.log('📍 Setting GPS to route start point...');
    window.updateGPS(
        window.routeSteps[0].lat, 
        window.routeSteps[0].lng, 
        window.routeSteps[0].heading || 0, 
        window.routeSteps[0].speed || 0
    );
    console.log('   Position set to:', window.routeSteps[0].lat.toFixed(5), window.routeSteps[0].lng.toFixed(5));
} else {
    console.error('❌ Monkey patch not installed! Copy and paste the monkey patch first.');
}

// Function to start the driving simulation
window.startSimulation = function() {
    if (typeof window.startDriving !== 'function') {
        console.error('❌ Monkey patch not installed!');
        return;
    }
    
    if (!window.routeSteps) {
        console.error('❌ Route steps not loaded!');
        return;
    }
    
    console.log('🚗 Starting simulation (~${speedKmh} km/h)...');
    console.log('   ' + window.routeSteps.length + ' steps');
    console.log('   Use window.stopDriving() to stop');
    
    // Start driving along the route
    window.startDriving(window.routeSteps);
};

console.log('✅ Route loaded with ' + window.routeSteps.length + ' steps');
console.log('');
console.log('📋 NEXT STEPS:');
console.log('   1. Click "START DRIVING" button');
console.log('   2. In console, run: startSimulation()');
console.log('');`;
};

/**
 * Component that generates route-specific simulation scripts
 */
const RouteScriptGenerator = () => {
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const script = generateRouteSimulationScript(routeCoordinates);
        navigator.clipboard.writeText(script).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const hasRoute = routeCoordinates && routeCoordinates.length >= 2;

    return (
        <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '10px', marginBottom: '5px', color: '#666' }}>
                Step 2: Generate & run route script
            </div>
            <button
                onClick={handleCopy}
                disabled={!hasRoute}
                style={{
                    width: '100%',
                    padding: '8px',
                    cursor: hasRoute ? 'pointer' : 'not-allowed',
                    backgroundColor: !hasRoute ? '#ccc' : (copied ? '#4CAF50' : '#FF9800'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}
            >
                {!hasRoute ? '⚠️ No Route' : (copied ? '✓ Copied!' : '🚗 Copy Route Script')}
            </button>
            <div style={{ fontSize: '9px', marginTop: '3px', color: '#999' }}>
                {hasRoute ? 'Paste into console to simulate drive' : 'Create a route first'}
            </div>
        </div>
    );
};

export default RouteScriptGenerator;
